import { Browser } from "@capacitor/browser";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import PKCE from "js-pkce";
import type {
	NavigationGuard,
	NavigationGuardNext,
	RouteLocationNormalized,
	RouteLocationNormalizedGeneric,
} from "vue-router";
import { getToken } from "../api";
import type {
	OrganizationDto,
	WellKnownDto,
} from "../flightpath-service-api-models";
import { initJohnDeereClient } from "./http-john-deere";

const appId = import.meta.env.VITE_JOHN_DEERE_APP_ID as string | undefined;
const scopes = import.meta.env.VITE_JOHN_DEERE_SCOPES as string | undefined;
const callbackUrl = import.meta.env.VITE_JOHN_DEERE_CALLBACK_URL as
	| string
	| undefined;

let accessToken = "";
let refreshToken = "";
let pkce: PKCE | null = null;
let tokenEndpoint = "";

export function isAuthenticated() {
	return accessToken !== "";
}

export function getJohnDeereToken() {
	return accessToken;
}

export async function authenticate(state: string, baseApiUrl: string) {
	try {
		const flightPathToken = await getToken();
		const rep = await getWellKnownResponse(flightPathToken, baseApiUrl);
		pkce = getPkce(rep);
		if (Capacitor.getPlatform() === "web") {
			window.location.replace(pkce.authorizeUrl({ state }));
			return;
		} else Browser.open({ url: pkce.authorizeUrl({ state }) });
	} catch (err) {
		console.error(err);
		return Promise.reject(err);
	}
}

export async function callbackHandler(
	to: RouteLocationNormalized,
	_from: RouteLocationNormalized,
	next: NavigationGuardNext,
	baseApiUrl: string,
	nextUrl?: string,
) {
	try {
		if (Capacitor.getPlatform() !== "web") Browser.close();
		const flightPathToken = await getToken();
		const state = to.query.state;
		const wellKnownRep = await getWellKnownResponse(
			flightPathToken,
			baseApiUrl,
		);
		pkce = getPkce(wellKnownRep as WellKnownDto);
		const currentPage = new URL(window.location.href);
		const url = new URL(to.fullPath, currentPage.origin).toString();

		const tokenRep: TokenResponse =
			Capacitor.getPlatform() === "android"
				? await exchangeTokenNatively(pkce, to)
				: await pkce.exchangeForAccessToken(url, { scope: scopes });
		if (!tokenRep) throw new Error("Token response is null or undefined.");
		if ((tokenRep as unknown as { error?: string }).error)
			throw new Error(`Token error: ${JSON.stringify(tokenRep)}`);
		if (!tokenRep.access_token)
			throw new Error(
				`Access token is null or undefined. tokenRep=${JSON.stringify(tokenRep)}`,
			);
		if (!tokenRep.refresh_token)
			throw new Error(
				`Refresh token is null or undefined. tokenRep=${JSON.stringify(tokenRep)}`,
			);

		accessToken = tokenRep.access_token;
		refreshToken = tokenRep.refresh_token;

		const nextUrlOrDefault = nextUrl ?? "/import";
		if (state === "undefined") next(nextUrlOrDefault);
		else next(`${nextUrlOrDefault}/${state}`);
	} catch (err) {
		console.error("[JD callback] err", err);
		next("/");
	}
}

function getCallbackUrl() {
	if (Capacitor.getPlatform() === "web") {
		return `${window.location.origin}/callback`;
	} else {
		if (!callbackUrl) throw new Error("Callback URL is not defined.");

		return callbackUrl;
	}
}

type TokenResponse = {
	access_token?: string;
	refresh_token?: string;
	error?: string;
};

// John Deere rejects browser requests (requests carrying an http(s) Origin header)
// unless that origin is a registered redirect URI. The Android WebView origin is
// https://localhost, which is not registered, so the token request is made through
// native HTTP, which sends no Origin header. iOS uses capacitor://localhost and web
// derives its redirect URI from the page origin, so neither is affected.
async function requestTokenNatively(
	grantType: string,
	params: Record<string, string | undefined>,
): Promise<TokenResponse> {
	const body = new URLSearchParams({ grant_type: grantType });
	for (const [key, value] of Object.entries(params)) {
		if (value != null) body.set(key, value);
	}

	const response = await CapacitorHttp.post({
		url: tokenEndpoint,
		headers: {
			Accept: "application/json",
			"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
		},
		data: body.toString(),
		responseType: "json",
	});
	if (response.status >= 400)
		throw new Error(`Token error: ${JSON.stringify(response.data)}`);

	return response.data as TokenResponse;
}

// exchangeForAccessToken normally parses the callback URL and validates the state, so
// the native path has to do both itself.
async function exchangeTokenNatively(
	pkceInstance: PKCE,
	to: RouteLocationNormalized,
) {
	const code = typeof to.query.code === "string" ? to.query.code : undefined;
	if (!code) throw new Error("Missing code in callback URL");
	if (to.query.state !== pkceInstance.getState())
		throw new Error("Invalid State");

	return requestTokenNatively("authorization_code", {
		code,
		client_id: appId,
		redirect_uri: getCallbackUrl(),
		code_verifier: pkceInstance.getCodeVerifier(),
		scope: scopes,
	});
}

function getPkce(rep: WellKnownDto) {
	if (!appId) throw new Error("John Deere app ID is not defined.");
	if (!scopes) throw new Error("John Deere scopes are not defined.");
	tokenEndpoint = rep.tokenEndpoint as string;
	return new PKCE({
		client_id: appId,
		redirect_uri: getCallbackUrl(),
		authorization_endpoint: rep.authorizationEndpoint as string,
		token_endpoint: rep.tokenEndpoint as string,
		requested_scopes: scopes,
		storage: localStorage,
	});
}

export async function refreshAccessToken() {
	try {
		const resp =
			Capacitor.getPlatform() === "android" && tokenEndpoint
				? await requestTokenNatively("refresh_token", {
						client_id: appId,
						refresh_token: refreshToken,
					})
				: await pkce?.refreshAccessToken(refreshToken);
		accessToken = resp?.access_token as string;
		refreshToken = resp?.refresh_token as string;
		return accessToken;
	} catch (error) {
		console.error(`Failed to refresh the John Deere token: ${error}`);
	}
}

async function getWellKnownResponse(
	flightPathToken: string,
	baseApiUrl: string,
): Promise<WellKnownDto> {
	const res = await fetch(
		`${baseApiUrl}/cloud-api/johndeere-integration/wellknown`,
		{
			method: "GET",
			headers: {
				authorization: `Bearer ${flightPathToken}`,
				Accept: "*/*",
				"Content-Type": "application/json",
			},
		},
	);

	return (await res.json()) as WellKnownDto;
}

export async function enableOrgAccessIfNeeded(
	orgId: string,
	baseApiUrl: string,
) {
	try {
		const flightPathToken = await getToken();
		await refreshAccessToken(); // Refresh the John Deere token
		const johnDeereToken = getJohnDeereToken() as string;
		const res = await fetch(
			`${baseApiUrl}/cloud-api/johndeere-integration/organizations/${orgId}`,
			{
				method: "GET",
				headers: {
					authorization: `Bearer ${flightPathToken}`,
					"X-FP-JohnDeere-Authorization": `Bearer ${johnDeereToken}`,
					Accept: "*/*",
					"Content-Type": "application/json",
				},
			},
		);

		const johnDeereOrganization = (await res.json()) as OrganizationDto;
		const link = johnDeereOrganization.urlConnections;

		if (link) {
			const params = new URLSearchParams({
				redirect_uri: window.location.href,
			});
			window.location.assign(`${link}?${params.toString()}`);
		}
	} catch (error) {
		console.log(`Failed to enable organization access: ${error}`);
	}
}

export const johnDeereAuthGuard: (
	apiUrl: string,
	paramName?: string,
) => NavigationGuard = (apiUrl, paramName) => async to => {
	if (!isAuthenticated()) {
		const param = paramName
			? ((to as RouteLocationNormalizedGeneric).params[
					paramName
				] as string)
			: "undefined";
		await authenticate(param, apiUrl);
		return false;
	} else {
		await refreshAccessToken();
		initJohnDeereClient(apiUrl, getJohnDeereToken());
		return true;
	}
};
